/**
 * WhatsAppQRCode Component
 *
 * Displays a QR code for WhatsApp Web pairing with countdown timer.
 * Shows expiration state and refresh action when the QR expires.
 *
 * Related Tickets: AC-1050
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, QrCode, Clock } from 'lucide-react';

interface WhatsAppQRCodeProps {
  qrData: string | null;
  expiresIn: number | null;
  onExpired: () => void;
  onRefresh: () => void;
}

const WhatsAppQRCode: React.FC<WhatsAppQRCodeProps> = ({
  qrData,
  expiresIn,
  onExpired,
  onRefresh,
}) => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(expiresIn);
  const [isExpired, setIsExpired] = useState(false);

  // Reset timer when new QR data or expiresIn arrives
  useEffect(() => {
    if (expiresIn !== null && expiresIn > 0) {
      setSecondsLeft(expiresIn);
      setIsExpired(false);
    }
  }, [expiresIn, qrData]);

  // Countdown timer — intentionally exclude secondsLeft from deps to avoid
  // re-creating the interval every second. The interval manages its own state
  // via the setSecondsLeft functional updater.
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setIsExpired(true);
          onExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExpired]);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Loading state
  if (!qrData && !isExpired) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="animate-pulse">
          <QrCode className="w-16 h-16 text-gray-300 dark:text-gray-600" />
        </div>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Generating QR code...
        </p>
      </div>
    );
  }

  // Expired state
  if (isExpired || (!qrData && isExpired)) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <QrCode className="w-16 h-16 text-gray-300 dark:text-gray-600" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          QR code expired
        </p>
        <button
          onClick={onRefresh}
          className="mt-3 inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 dark:bg-primary-900/20 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh QR Code
        </button>
      </div>
    );
  }

  // Active QR code
  return (
    <div className="flex flex-col items-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="bg-white p-3 rounded-lg">
        <img
          src={`data:image/png;base64,${qrData}`}
          alt="WhatsApp QR Code"
          className="w-48 h-48"
        />
      </div>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
        Scan this QR code with WhatsApp on your phone
      </p>
      {secondsLeft !== null && secondsLeft > 0 && (
        <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
          <Clock className="w-3 h-3 mr-1" />
          Expires in {formatTime(secondsLeft)}
        </div>
      )}
    </div>
  );
};

export default WhatsAppQRCode;
